import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { WorkspaceKind } from '@hookmyapp/shared';
import { useFetch } from '@/hooks/use-fetch';
import { rangeFor, type RangePreset, type CustomDates, type ChannelStatusFilter } from '@/pages/dashboard/use-dashboard-metrics';

export interface OrgWorkspaceMetrics {
  id: string;
  name: string;
  channels: number;
  /** Usage over the selected range, in the org's meter (AIT-436). */
  used: number;
}

export interface OrgDashboardMetrics {
  channels: number;
  channelsDisconnected: number;
  used: number;
  usageUnit: 'messages' | 'actions';
  daily: Array<{ date: string; count: number }>;
  workspaces: OrgWorkspaceMetrics[];
}

// dispatch is not tracked by react-hooks/set-state-in-effect, so a synchronous
// start dispatch in the effect path is safe.
type OrgMetricsState = { data: OrgDashboardMetrics | null; loaded: boolean };
type OrgMetricsAction = { type: 'start' } | { type: 'reset' } | { type: 'done'; data: OrgDashboardMetrics | null };

function orgMetricsReducer(state: OrgMetricsState, action: OrgMetricsAction): OrgMetricsState {
  switch (action.type) {
    case 'start': return { ...state, loaded: false };
    // Waiting for a new quota window: drop the previous org/workspace's numbers
    // so a failed usage fetch cannot leave them on screen (AIT-629).
    case 'reset': return { data: null, loaded: false };
    case 'done': return { data: action.data, loaded: true };
  }
}

export function useOrgDashboard(
  orgId: string, preset: RangePreset, custom?: CustomDates, status: ChannelStatusFilter = 'all',
  kind?: WorkspaceKind, periodStart?: string,
) {
  const fetchWithAuth = useFetch();
  const [{ data, loaded }, dispatch] = useReducer(orgMetricsReducer, { data: null, loaded: false });

  // Extract primitives so exhaustive-deps is satisfied without capturing the
  // whole `custom` object (which can be recreated each render by the parent).
  const start = custom?.start;
  const end = custom?.end;

  // AIT-629: `periodStart` arrives async, so a cold 'period' load fires 7d
  // first and the real range second. Only the latest request may dispatch.
  const seq = useRef(0);
  const load = useCallback(async () => {
    // 'custom' with incomplete dates: skip the fetch until both are picked.
    // This early return must come BEFORE any dispatch so the skip path issues
    // neither a request nor a 'start' action.
    if (preset === 'custom' && (!start || !end)) return;
    // 'period' before the usage response: wait rather than show 7d totals
    // under a "Billing period" tab (the org-usage query is fail-soft, so a
    // failure here keeps the skeleton instead of lying).
    if (preset === 'period' && !periodStart) {
      ++seq.current; // an older in-flight response must not land either
      dispatch({ type: 'reset' });
      return;
    }
    const mine = ++seq.current;
    dispatch({ type: 'start' });
    const { from, to } = rangeFor(preset, start && end ? { start, end } : undefined, periodStart);
    // Non-critical widget: a 5xx/network blip on the org-metrics read must
    // render empty metrics, NOT escalate to the global ErrorPage and tear the
    // org dashboard down. suppressGlobalError + a local catch is the codebase's
    // fail-soft contract (see use-fetch.ts; mirrors use-dashboard-metrics.ts).
    try {
      const res = await fetchWithAuth(
        `/organizations/${orgId}/dashboard-metrics?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&status=${status}${kind ? `&kind=${kind}` : ''}`,
        undefined,
        { suppressGlobalError: true },
      );
      // dispatch is stable across renders (React guarantee) — no need to list it in deps.
      const data = res.ok ? await res.json() : null;
      if (mine === seq.current) dispatch({ type: 'done', data });
    } catch {
      if (mine === seq.current) dispatch({ type: 'done', data: null });
    }
  }, [fetchWithAuth, orgId, preset, start, end, status, kind, periodStart]);

  useEffect(() => { void load(); }, [load]);

  // Derive loading: true on initial mount until first fetch resolves, false
  // immediately when the skip condition is active (no request will be issued).
  const skip = preset === 'custom' && (!start || !end);
  return { data, loading: !loaded && !skip, reload: load };
}
